import { useState } from 'react';
import { useQuery } from 'react-query';
import {
  MagnifyingGlassIcon,
  BuildingOfficeIcon,
  UsersIcon,
  ChatBubbleLeftIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../components/LoadingSpinner';
import PostFeed from '../components/PostFeed';
import { clubAPI } from '../services/api';

const ClubsPage = () => {
  const [filters, setFilters] = useState({
    search: '',
    active: '',
    page: 1,
  });
  const [selectedClub, setSelectedClub] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [activeTab, setActiveTab] = useState('members');

  // Fetch clubs
  const { data, isLoading, error, refetch } = useQuery(
    ['clubs', filters],
    () => clubAPI.getClubs({
      ...filters,
      active: filters.active === '' ? undefined : filters.active,
    }),
    {
      keepPreviousData: true,
    }
  );

  // Fetch club details with activities and discussions
  const { data: clubDetails, isLoading: isLoadingDetails } = useQuery(
    ['clubDetails', selectedClub?._id],
    () => clubAPI.getClubDetails(selectedClub._id),
    {
      enabled: !!selectedClub?._id && showDetailsModal,
    }
  );

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1,
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleViewDetails = (club) => {
    setSelectedClub(club);
    setShowDetailsModal(true);
    setActiveTab('members');
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const postTime = new Date(timestamp);
    const diffInSeconds = Math.floor((now - postTime) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return postTime.toLocaleDateString();
  };

  if (isLoading) return <LoadingSpinner text="Loading clubs..." />;

  if (error) {
    console.error('❌ Clubs Error:', error);
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">Error loading clubs</p>
        <p className="text-sm text-gray-500 mb-4">{error.message}</p>
        <button onClick={() => refetch()} className="btn-primary">
          Try Again
        </button>
      </div>
    );
  }

  const { clubs = [], pagination = {} } = data?.data?.data || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Club Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Monitor and manage all clubs in your college
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search clubs..."
              className="input pl-10"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>

          {/* Status Filter */}
          <div>
            <select
              className="input"
              value={filters.active}
              onChange={(e) => handleFilterChange('active', e.target.value)}
            >
              <option value="">All Clubs</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          {/* Reset Filters */}
          <button
            onClick={() => setFilters({ search: '', active: '', page: 1 })}
            className="btn-secondary"
          >
            Reset Filters
          </button>
        </div>
      </div>

      {/* Clubs Grid */}
      <div className="space-y-4">
        {clubs.length === 0 ? (
          <div className="text-center py-12">
            <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No clubs found</h3>
            <p className="mt-1 text-sm text-gray-500">
              No clubs match the current filters.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {clubs.map((club) => (
              <div key={club._id} className="card p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="text-2xl">{club.logo}</span>
                      </div>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg font-medium text-gray-900 truncate">
                        {club.name}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {club.facultyMentor?.name || 'No mentor assigned'}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`badge ${
                      club.isActive ? 'badge-green' : 'badge-red'
                    }`}
                  >
                    {club.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <p className="mt-4 text-sm text-gray-600 line-clamp-3">
                  {club.description}
                </p>

                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center text-sm text-gray-500">
                    <UsersIcon className="h-4 w-4 mr-1" />
                    <span>{club.members?.length || 0} members</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {club.pendingApplicationsCount > 0 && (
                      <span className="badge badge-yellow">
                        {club.pendingApplicationsCount} pending
                      </span>
                    )}
                    <button
                      onClick={() => handleViewDetails(club)}
                      className="text-primary-600 hover:text-primary-900 text-sm font-medium"
                    >
                      View Details
                    </button>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>Created: {new Date(club.createdAt).toLocaleDateString()}</span>
                    <span>Mentor: {club.facultyMentor?.branch}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Showing {(pagination.current - 1) * 20 + 1} to{' '}
              {Math.min(pagination.current * 20, pagination.total)} of {pagination.total} results
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange(pagination.current - 1)}
                disabled={!pagination.hasPrev}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-3 py-2 text-sm text-gray-600">
                Page {pagination.current} of {pagination.pages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.current + 1)}
                disabled={!pagination.hasNext}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Club Details Modal */}
      {showDetailsModal && selectedClub && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-[900px] max-h-[90vh] shadow-lg rounded-md bg-white overflow-hidden flex flex-col">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="h-16 w-16 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-3xl">{selectedClub.logo}</span>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-2xl font-bold text-gray-900">{selectedClub.name}</h3>
                    <p className="text-gray-600">Faculty Mentor: {selectedClub.facultyMentor?.name}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Tab Navigation */}
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                  <button
                    onClick={() => setActiveTab('members')}
                    className={`${
                      activeTab === 'members'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                  >
                    <UsersIcon className="h-5 w-5 mr-2" />
                    Members ({selectedClub.members?.length || 0})
                  </button>
                  <button
                    onClick={() => setActiveTab('activities')}
                    className={`${
                      activeTab === 'activities'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                  >
                    <DocumentTextIcon className="h-5 w-5 mr-2" />
                    Activities ({clubDetails?.data?.activities?.length || 0})
                  </button>
                  <button
                    onClick={() => setActiveTab('discussions')}
                    className={`${
                      activeTab === 'discussions'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                  >
                    <ChatBubbleLeftIcon className="h-5 w-5 mr-2" />
                    Discussions ({clubDetails?.data?.discussions?.length || 0})
                  </button>
                </nav>
              </div>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto mt-6">
              {isLoadingDetails && activeTab !== 'members' ? (
                <div className="flex justify-center items-center h-48">
                  <LoadingSpinner text="Loading..." />
                </div>
              ) : (
                <>
                  {/* Members Tab */}
                  {activeTab === 'members' && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Club Info */}
                        <div>
                          <h4 className="text-lg font-medium text-gray-900 mb-3">Club Information</h4>
                          <div className="space-y-3">
                            <div>
                              <label className="text-sm font-medium text-gray-500">Description</label>
                              <p className="text-sm text-gray-900">{selectedClub.description}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-500">Status</label>
                              <p>
                                <span className={`badge ${selectedClub.isActive ? 'badge-green' : 'badge-red'}`}>
                                  {selectedClub.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-500">Created</label>
                              <p className="text-sm text-gray-900">
                                {new Date(selectedClub.createdAt).toLocaleDateString()}
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-500">Created By</label>
                              <p className="text-sm text-gray-900">{selectedClub.createdBy?.name}</p>
                            </div>
                          </div>
                        </div>

                        {/* Statistics */}
                        <div>
                          <h4 className="text-lg font-medium text-gray-900 mb-3">Statistics</h4>
                          <div className="space-y-3">
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-500">Total Members</span>
                              <span className="text-sm font-medium text-gray-900">
                                {selectedClub.members?.length || 0}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-500">Pending Applications</span>
                              <span className="text-sm font-medium text-gray-900">
                                {selectedClub.pendingApplicationsCount || 0}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-500">Quiz Questions</span>
                              <span className="text-sm font-medium text-gray-900">
                                {selectedClub.quiz?.length || 0}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Members List */}
                      {selectedClub.members && selectedClub.members.length > 0 && (
                        <div>
                          <h4 className="text-lg font-medium text-gray-900 mb-3">Club Members</h4>
                          <div className="space-y-2">
                            {selectedClub.members.map((member) => (
                              <div key={member._id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                                <div className="flex items-center">
                                  <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                    <span className="text-sm font-medium text-gray-600">
                                      {member.user?.name?.charAt(0)?.toUpperCase() || 'U'}
                                    </span>
                                  </div>
                                  <div className="ml-3">
                                    <p className="text-sm font-medium text-gray-900">
                                      {member.user?.name || 'Unknown User'}
                                    </p>
                                    <p className="text-xs text-gray-500">{member.user?.email}</p>
                                  </div>
                                </div>
                                <span className="badge badge-blue">{member.role}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Activities Tab */}
                  {activeTab === 'activities' && (
                    <div>
                      {clubDetails?.data?.activities && clubDetails.data.activities.length > 0 ? (
                        <PostFeed posts={clubDetails.data.activities} showDeleteButton={false} />
                      ) : (
                        <div className="text-center py-12">
                          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                          <h3 className="mt-2 text-sm font-medium text-gray-900">No activities</h3>
                          <p className="mt-1 text-sm text-gray-500">This club hasn't posted any activities yet.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Discussions Tab */}
                  {activeTab === 'discussions' && (
                    <div>
                      {clubDetails?.data?.discussions && clubDetails.data.discussions.length > 0 ? (
                        <div className="space-y-4">
                          {clubDetails.data.discussions.map((discussion) => (
                            <div key={discussion._id} className="card p-6 hover:shadow-md transition-shadow">
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <h4 className="text-lg font-semibold text-gray-900">{discussion.title}</h4>
                                    <span className="badge badge-blue text-xs">{discussion.category}</span>
                                    {discussion.isPinned && (
                                      <span className="badge badge-yellow text-xs">Pinned</span>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-600 mb-3">{discussion.content}</p>
                                  <div className="flex items-center text-xs text-gray-500 space-x-4">
                                    <span>By {discussion.author?.name || 'Unknown'}</span>
                                    <span>•</span>
                                    <span>{formatTimeAgo(discussion.createdAt || discussion.timestamp)}</span>
                                    <span>•</span>
                                    <span>{discussion.repliesCount || discussion.replies?.length || 0} replies</span>
                                  </div>
                                </div>
                              </div>

                              {/* Replies Preview */}
                              {discussion.replies && discussion.replies.length > 0 && (
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                  <h5 className="text-sm font-medium text-gray-700 mb-2">Recent Replies</h5>
                                  <div className="space-y-2">
                                    {discussion.replies.slice(0, 2).map((reply, idx) => (
                                      <div key={reply._id || idx} className="bg-gray-50 p-3 rounded text-sm">
                                        <div className="flex items-center mb-1">
                                          <span className="font-medium text-gray-900">
                                            {typeof reply.author === 'object' ? reply.author?.name : 'Anonymous'}
                                          </span>
                                          <span className="text-gray-400 text-xs ml-2">
                                            {formatTimeAgo(reply.timestamp)}
                                          </span>
                                        </div>
                                        <p className="text-gray-700">{reply.content}</p>
                                      </div>
                                    ))}
                                    {discussion.replies.length > 2 && (
                                      <p className="text-xs text-gray-500 italic">
                                        +{discussion.replies.length - 2} more replies
                                      </p>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <ChatBubbleLeftIcon className="mx-auto h-12 w-12 text-gray-400" />
                          <h3 className="mt-2 text-sm font-medium text-gray-900">No discussions</h3>
                          <p className="mt-1 text-sm text-gray-500">No discussions have been started in this club yet.</p>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="btn-secondary w-full"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClubsPage;